import React, { lazy, Suspense, useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { captureUtm, initTracking } from "@/lib/analytics";

captureUtm();
import { Toaster } from "sonner";
import "@/App.css";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { SettingsProvider } from "@/context/SettingsContext";
import Landing from "@/pages/Landing";
import Auth from "@/pages/Auth";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import Dashboard from "@/pages/Dashboard";
import Products from "@/pages/Products";
import ProductDetail from "@/pages/ProductDetail";
import MyLeads from "@/pages/MyLeads";
import Team from "@/pages/Team";
import Earnings from "@/pages/Earnings";
import Withdraw from "@/pages/Withdraw";
import Profile from "@/pages/Profile";
// Heavy admin / staff bundles — lazy-loaded so partner first-paint isn't
// blocked by Recharts + 2.5k-line Admin.js. Each opens a separate chunk.
const Admin = lazy(() => import("@/pages/Admin"));
const Operations = lazy(() => import("@/pages/Operations"));
const Finance = lazy(() => import("@/pages/Finance"));
const PLDashboard = lazy(() => import("@/pages/PLDashboard"));
const Protect = lazy(() => import("@/pages/Protect"));
const RentSafe = lazy(() => import("@/pages/RentSafe"));
const RentSafeMembership = lazy(() => import("@/pages/RentSafeMembership"));
const NewOfflineQuote = lazy(() => import("@/pages/agent/NewOfflineQuote"));
const MyClaims = lazy(() => import("@/pages/agent/MyClaims"));
const PolicyLens = lazy(() => import("@/pages/PolicyLens"));
const CardLens = lazy(() => import("@/pages/CardLens"));
const PremiumCalculator = lazy(() => import("@/pages/PremiumCalculator"));
const Renewals = lazy(() => import("@/pages/Renewals"));
const ClientCleanup = lazy(() => import("@/pages/ClientCleanup"));
const ScanConsent = lazy(() => import("@/pages/ScanConsent"));
const FieldHome = lazy(() => import("@/pages/field/FieldHome"));
const FieldAttendance = lazy(() => import("@/pages/field/FieldAttendance"));
const FieldOnboard = lazy(() => import("@/pages/field/FieldOnboard"));
const FieldConsultants = lazy(() => import("@/pages/field/FieldTeam").then((m) => ({ default: m.FieldConsultants })));
const FieldScoreboard = lazy(() => import("@/pages/field/FieldTeam").then((m) => ({ default: m.FieldScoreboard })));
const LeadMarket = lazy(() => import("@/pages/LeadMarket"));
const CallerPortal = lazy(() => import("@/pages/caller/CallerPortal"));
const LendingPortal = lazy(() => import("@/pages/lending/LendingPortal"));
const CapitalPortal = lazy(() => import("@/pages/capital/CapitalPortal"));
const DailyAdvance = lazy(() => import("@/pages/DailyAdvance"));
const BulkBusiness = lazy(() => import("@/pages/BulkBusiness"));
const Company = lazy(() => import("@/pages/Company"));
const EmployeeDashboard = lazy(() => import("@/pages/EmployeeDashboard"));
const PartnerHubPortal = lazy(() => import("@/pages/hub/PartnerHubPortal"));
const NewClaim = lazy(() => import("@/pages/agent/NewClaim"));
const ClaimDetail = lazy(() => import("@/pages/agent/ClaimDetail"));
const VendorPortal = lazy(() => import("@/pages/vendor/VendorPortal"));
const VendorDashboard = lazy(() => import("@/pages/vendor/VendorDashboard"));
const TrackClaim = lazy(() => import("@/pages/TrackClaim"));
const InsuranceDiscovery = lazy(() => import("@/pages/InsuranceDiscovery"));
const Marketing = lazy(() => import("@/pages/Marketing"));
const About = lazy(() => import("@/pages/About"));
const PrivacyPolicy = lazy(() => import("@/pages/PrivacyPolicy"));
const GoRedirect = lazy(() => import("@/pages/GoRedirect"));
const PublisherSignup = lazy(() => import("@/pages/publisher/PublisherAuth").then((m) => ({ default: m.PublisherSignup })));
const PublisherLogin = lazy(() => import("@/pages/publisher/PublisherAuth").then((m) => ({ default: m.PublisherLogin })));
const PublisherPortal = lazy(() => import("@/pages/publisher/PublisherPortal"));
const TermsPage = lazy(() => import("@/pages/Terms"));
const MyQuotes = lazy(() => import("@/pages/agent/MyQuotes"));
const QuoteDetail = lazy(() => import("@/pages/agent/QuoteDetail"));
const SaasLanding = lazy(() => import("@/pages/saas/SaasLanding"));
const SaasSignup = lazy(() => import("@/pages/saas/SaasSignup"));
const SaasLogin = lazy(() => import("@/pages/saas/SaasLogin"));
const SaasDashboard = lazy(() => import("@/pages/saas/SaasDashboard"));
const SaasSuperAdmin = lazy(() => import("@/pages/saas/SaasSuperAdmin"));
const SaasPortal = lazy(() => import("@/pages/saas/PortalPage"));
const WorkspaceAdmin = lazy(() => import("@/pages/saas/WorkspaceAdmin"));
const WorkspaceAdminGate = lazy(() => import("@/pages/saas/WorkspaceAdminGate"));
const PortalLogin = lazy(() => import("@/pages/saas/PortalLogin"));
const PortalApp = lazy(() => import("@/pages/saas/PortalApp"));
import AdminLogin from "@/pages/AdminLogin";
import AdminForgotPassword from "@/pages/AdminForgotPassword";
import AdminResetPassword from "@/pages/AdminResetPassword";
import OperationsLogin from "@/pages/OperationsLogin";
import FinanceLogin from "@/pages/FinanceLogin";
import PLLogin from "@/pages/PLLogin";
import Flyer from "@/pages/Flyer";
import Contests from "@/pages/Contests";
import VisitingCard from "@/pages/VisitingCard";
import Certificates from "@/pages/Certificates";
import Training from "@/pages/Training";
import TrainingExam from "@/pages/TrainingExam";
import MyBranch from "@/pages/MyBranch";
import Clients from "@/pages/Clients";
import ClientDetail from "@/pages/ClientDetail";
import ReferralLanding from "@/pages/ReferralLanding";
import ClientStorefront from "@/pages/ClientStorefront";
import DashboardLayout from "@/components/layout/DashboardLayout";
import { TourProvider } from "@/components/ProductTour";
import ImpersonationBanner from "@/components/ImpersonationBanner";
import { InstallPrompt } from "@/components/InstallPrompt";

const PageLoader = () => (
  <div className="min-h-[60vh] flex items-center justify-center text-stone-500 text-sm">Loading…</div>
);
const lazyWrap = (el) => <Suspense fallback={<PageLoader />}>{el}</Suspense>;

function Protected({ children, adminOnly }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen flex items-center justify-center text-stone-500">Loading...</div>;
  if (!user) return <Navigate to="/auth" replace />;
  // Admin pages: `admin`, `cxo` and `ceo` roles can all access.
  if (adminOnly && user.role !== "admin" && user.role !== "cxo" && user.role !== "ceo") return <Navigate to="/app/dashboard" replace />;
  return children;
}

function RoleGuard({ role, redirectTo, children }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen flex items-center justify-center text-stone-500">Loading...</div>;
  // Admins AND the CEO can enter any role-guarded route — CEO needs full access.
  if (!user || (user.role !== role && user.role !== "admin" && user.role !== "ceo")) return <Navigate to={redirectTo} replace />;
  return children;
}

/** Lands the user on the right "home" tab based on their role.
 *  - Admin / CEO / CXO → admin control panel (where they actually work)
 *  - Partner → the agent dashboard
 *  This is what `/app` redirects to, so a page refresh or stale bookmark
 *  never dumps a CEO onto the agent dashboard.
 */
function SignupRedirect() {
  const params = new URLSearchParams(window.location.search);
  params.set("mode", "signup");
  return <Navigate to={`/auth?${params.toString()}`} replace />;
}

function AppIndexRedirect() {
  const { user } = useAuth();
  const isAdminLike = user && (user.role === "admin" || user.role === "ceo" || user.role === "cxo");
  const isPL = user && user.role === "pl_partner";
  const home = isAdminLike ? "/app/admin" : isPL ? "/app/pl" : user?.role === "sales" ? "/app/field" : "/app/dashboard";
  return <Navigate to={home} replace />;
}

// Corporate employees see a payout-free dashboard and are bounced off every money page.
function DashboardSwitch() {
  const { user } = useAuth();
  return user?.corporate_id ? <EmployeeDashboard /> : <Dashboard />;
}

function NoEmployee({ children }) {
  const { user } = useAuth();
  return user?.corporate_id ? <Navigate to="/app/dashboard" replace /> : children;
}

function App() {
  useEffect(() => {
    initTracking();
  }, []);
  return (
    <AuthProvider>
      <SettingsProvider>
      <BrowserRouter>
        <Toaster position="top-right" richColors duration={3500} closeButton />
        <InstallPrompt />
        <ImpersonationBanner />
        <TourProvider>
        <Routes>
          <Route path="/" element={<Landing />} />
          <Route path="/about" element={lazyWrap(<About />)} />
          <Route path="/privacy" element={lazyWrap(<PrivacyPolicy />)} />
          <Route path="/go/:code" element={lazyWrap(<GoRedirect />)} />
          <Route path="/publishers" element={lazyWrap(<PublisherSignup />)} />
          <Route path="/publisher/login" element={lazyWrap(<PublisherLogin />)} />
          <Route path="/publisher" element={lazyWrap(<PublisherPortal />)} />
          <Route path="/terms" element={lazyWrap(<TermsPage />)} />
          <Route path="/protect" element={lazyWrap(<Protect />)} />
          <Route path="/rentsafe" element={lazyWrap(<RentSafe />)} />
          <Route path="/rentsafe/membership/:id" element={lazyWrap(<RentSafeMembership />)} />
          {/* Crece SaaS multi-tenant platform */}
          <Route path="/saas" element={lazyWrap(<SaasLanding />)} />
          <Route path="/saas/signup" element={lazyWrap(<SaasSignup />)} />
          <Route path="/saas/login" element={lazyWrap(<SaasLogin />)} />
          <Route path="/saas/dashboard" element={lazyWrap(<SaasDashboard />)} />
          <Route path="/saas/admin" element={<RoleGuard role="admin" redirectTo="/admin">{lazyWrap(<SaasSuperAdmin />)}</RoleGuard>} />
          <Route path="/saas/portal/:slug" element={lazyWrap(<SaasPortal />)} />
          <Route path="/portal-admin" element={lazyWrap(<WorkspaceAdminGate />)} />
          <Route path="/portal/:slug/admin" element={lazyWrap(<WorkspaceAdmin />)} />
          <Route path="/portal/:slug/login" element={lazyWrap(<PortalLogin />)} />
          <Route path="/portal/:slug/app" element={lazyWrap(<PortalApp />)} />
          <Route path="/r/:code" element={<ReferralLanding />} />
          <Route path="/vendor" element={lazyWrap(<VendorPortal />)} />
          <Route path="/vendor/dashboard" element={lazyWrap(<VendorDashboard />)} />
          <Route path="/track/:token" element={lazyWrap(<TrackClaim />)} />
          <Route path="/c/:code" element={<ClientStorefront />} />
          <Route path="/scan/:token" element={lazyWrap(<ScanConsent />)} />
          <Route path="/auth" element={<Auth />} />
          <Route path="/signup" element={<SignupRedirect />} />
          <Route path="/join" element={<SignupRedirect />} />
          <Route path="/auth/forgot" element={<ForgotPassword />} />
          <Route path="/auth/reset" element={<ResetPassword />} />
          <Route path="/admin" element={<AdminLogin />} />
          <Route path="/admin/forgot" element={<AdminForgotPassword />} />
          <Route path="/admin/reset" element={<AdminResetPassword />} />
          <Route path="/crece-control-x7q9" element={<AdminLogin />} />
          <Route path="/operations" element={<OperationsLogin />} />
          <Route path="/operations/dashboard" element={<RoleGuard role="operations" redirectTo="/operations">{lazyWrap(<Operations />)}</RoleGuard>} />
          <Route path="/finance" element={<FinanceLogin />} />
          <Route path="/finance/dashboard" element={<RoleGuard role="finance" redirectTo="/finance">{lazyWrap(<Finance />)}</RoleGuard>} />
          <Route path="/pl-login" element={<PLLogin />} />
          <Route path="/caller" element={lazyWrap(<CallerPortal />)} />
          <Route path="/lending" element={lazyWrap(<LendingPortal />)} />
          <Route path="/capital" element={lazyWrap(<CapitalPortal />)} />
          <Route path="/hub" element={lazyWrap(<PartnerHubPortal />)} />
          <Route path="/pl" element={<PLLogin />} />
          <Route path="/flyer" element={<Protected><Flyer /></Protected>} />
          <Route path="/card" element={<Protected><VisitingCard /></Protected>} />
          <Route path="/app" element={<Protected><DashboardLayout /></Protected>}>
            <Route index element={<AppIndexRedirect />} />
            <Route path="dashboard" element={lazyWrap(<DashboardSwitch />)} />
            <Route path="company" element={lazyWrap(<Company />)} />
            <Route path="daily-advance" element={<NoEmployee>{lazyWrap(<DailyAdvance />)}</NoEmployee>} />
            <Route path="bulk-business" element={<NoEmployee>{lazyWrap(<BulkBusiness />)}</NoEmployee>} />
            <Route path="products" element={<Products />} />
            <Route path="products/:id" element={<ProductDetail />} />
            <Route path="leads" element={<MyLeads />} />
            <Route path="team" element={<NoEmployee><Team /></NoEmployee>} />
            <Route path="earnings" element={<NoEmployee><Earnings /></NoEmployee>} />
            <Route path="withdraw" element={<NoEmployee><Withdraw /></NoEmployee>} />
            <Route path="contests" element={<NoEmployee><Contests /></NoEmployee>} />
            <Route path="training" element={<Training />} />
            <Route path="training/exam" element={<TrainingExam />} />
            <Route path="certificates" element={<Certificates />} />
            <Route path="my-branch" element={<MyBranch />} />
            <Route path="clients" element={<Clients />} />
            <Route path="clients/cleanup" element={lazyWrap(<ClientCleanup />)} />
            <Route path="renewals" element={lazyWrap(<Renewals />)} />
            <Route path="field" element={lazyWrap(<FieldHome />)} />
            <Route path="field/attendance" element={lazyWrap(<FieldAttendance />)} />
            <Route path="field/onboard" element={lazyWrap(<FieldOnboard />)} />
            <Route path="field/consultants" element={lazyWrap(<FieldConsultants />)} />
            <Route path="field/scoreboard" element={lazyWrap(<FieldScoreboard />)} />
            <Route path="clients/:id" element={<ClientDetail />} />
            <Route path="clients/:id/discovery" element={lazyWrap(<InsuranceDiscovery />)} />
            <Route path="marketing" element={<NoEmployee>{lazyWrap(<Marketing />)}</NoEmployee>} />
            <Route path="pl" element={lazyWrap(<PLDashboard />)} />
            <Route path="quotes" element={lazyWrap(<MyQuotes />)} />
            <Route path="quotes/new" element={lazyWrap(<NewOfflineQuote />)} />
            <Route path="quotes/:id" element={lazyWrap(<QuoteDetail />)} />
            <Route path="claims" element={lazyWrap(<MyClaims />)} />
            <Route path="policy-lens" element={lazyWrap(<PolicyLens />)} />
            <Route path="card-lens" element={lazyWrap(<CardLens />)} />
            <Route path="premium-calculator" element={lazyWrap(<PremiumCalculator />)} />
            <Route path="growth-exchange" element={<NoEmployee>{lazyWrap(<LeadMarket />)}</NoEmployee>} />
            <Route path="lead-market" element={lazyWrap(<LeadMarket />)} />
            <Route path="one-clients" element={<Navigate to="/app/clients" replace />} />
            <Route path="claims/new" element={lazyWrap(<NewClaim />)} />
            <Route path="claims/:id" element={lazyWrap(<ClaimDetail />)} />
            <Route path="profile" element={<Profile />} />
            <Route path="admin" element={<Protected adminOnly>{lazyWrap(<Admin />)}</Protected>} />
          </Route>
        </Routes>
        </TourProvider>
      </BrowserRouter>
      </SettingsProvider>
    </AuthProvider>
  );
}

export default App;
